import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// NOTE: Tokens stored in localStorage because httpOnly cookies don't work cross-domain
// (gameboxmods.com frontend -> emergent.host API). XSS risk is mitigated by:
// 1. No user-generated HTML rendered with dangerouslySetInnerHTML
// 2. React auto-escapes all rendered content
// 3. Content-Security-Policy headers recommended for production
axios.defaults.withCredentials = false;

function setStoredToken(token, refresh) {
  if (token) {
    localStorage.setItem("ogxbox_token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    localStorage.removeItem("ogxbox_token");
    delete axios.defaults.headers.common["Authorization"];
  }
  if (refresh !== undefined) {
    if (refresh) {
      localStorage.setItem("ogxbox_refresh", refresh);
    } else {
      localStorage.removeItem("ogxbox_refresh");
    }
  }
}

const savedToken = localStorage.getItem("ogxbox_token");
if (savedToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
}

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshToken = useCallback(async () => {
    try {
      const rt = localStorage.getItem("ogxbox_refresh");
      const { data } = await axios.post(`${API}/api/auth/refresh`, { refresh_token: rt || "" });
      if (data.access_token) {
        setStoredToken(data.access_token);
      }
      return true;
    } catch (err) {
      console.error("Token refresh failed:", err.message || err);
      return false;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`);
      setUser(data);
    } catch (err) {
      console.error("Auth check failed:", err.message || err);
      const refreshed = await refreshToken();
      if (refreshed) {
        try {
          const { data } = await axios.get(`${API}/api/auth/me`);
          setUser(data);
          return;
        } catch (retryErr) {
          console.error("Auth retry after refresh failed:", retryErr.message || retryErr);
        }
      }
      setUser(false);
      setStoredToken(null);
    } finally {
      setLoading(false);
    }
  }, [refreshToken]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry &&
            !originalRequest.url?.includes('/auth/login') &&
            !originalRequest.url?.includes('/auth/register') &&
            !originalRequest.url?.includes('/auth/refresh')) {
          originalRequest._retry = true;
          const refreshed = await refreshToken();
          if (refreshed) {
            const token = localStorage.getItem("ogxbox_token");
            if (token) {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
            }
            return axios(originalRequest);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [refreshToken]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      if (data.access_token) {
        setStoredToken(data.access_token, data.refresh_token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name, referral_source = "", turnstile_token = "") => {
    try {
      const { data } = await axios.post(`${API}/api/auth/register`, { email, password, name, origin_url: window.location.origin, referral_source, turnstile_token });
      if (data.access_token) {
        setStoredToken(data.access_token, data.refresh_token);
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {});
    } catch (err) {
      console.error("Logout request failed:", err.message || err);
    }
    setUser(false);
    setStoredToken(null, null);
  };

  const value = useMemo(() => ({
    user, loading, login, register, logout, checkAuth
  }), [user, loading, checkAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
